const PERFORMANCE_CATEGORY_MATCHERS = [
  /^performance\s*&\s*functionality$/i,
  /^performance_and_functionality$/i,
  /^performance_function$/i,
];

const SECURITI_CONSENT_COOKIE = '__privaci_cookie_consents';
const CONSENT_SAVE_SETTLE_POLL_MS = 200;
const CONSENT_SAVE_SETTLE_TIMEOUT_MS = 3000;
const CRISP_WIDGET_SELECTOR = '.crisp-client, #crisp-chatbox';

let consentSaveReloadScheduled = false;
let crispTagRequested = false;

export const matchesPerformanceCategoryName = (name) => {
  if (!name || typeof name !== 'string') {
    return false;
  }

  const trimmed = name.trim();

  if (PERFORMANCE_CATEGORY_MATCHERS.some((matcher) => matcher.test(trimmed))) {
    return true;
  }

  const normalized = trimmed.toLowerCase();

  return (
    normalized.includes('performance') &&
    (normalized.includes('function') || normalized.includes('functionality'))
  );
};

const matchesPerformanceCategory = (category) => {
  if (!category) {
    return false;
  }

  return (
    matchesPerformanceCategoryName(category.name) ||
    matchesPerformanceCategoryName(category.base_category)
  );
};

const readRawCookie = (name) => {
  const match = document.cookie.match(
    new RegExp(
      `(?:^|;\\s*)${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`,
    ),
  );

  return match ? match[1] : null;
};

const readSecuritiConsentCookie = () => {
  if (typeof window.bannerConfigUtils?.getCookieValue === 'function') {
    return window.bannerConfigUtils.getCookieValue(SECURITI_CONSENT_COOKIE);
  }

  const rawCookie = readRawCookie(SECURITI_CONSENT_COOKIE);

  if (!rawCookie) {
    return null;
  }

  try {
    return decodeURIComponent(rawCookie);
  } catch (e) {
    return rawCookie;
  }
};

const getSecuritiCategories = () => {
  if (typeof window.bannerConfigUtils?.getParams !== 'function') {
    return [];
  }

  return window.bannerConfigUtils.getParams().categories || [];
};

const getPerformanceCategoryIds = () => {
  const ids = new Set();
  const categories = getSecuritiCategories();

  categories.filter(matchesPerformanceCategory).forEach((category) => {
    if (category?.id !== undefined && category?.id !== null) {
      ids.add(String(category.id));
    }
  });

  document
    .querySelectorAll('[data-category-id][data-category-original]')
    .forEach((node) => {
      const originalName = node.getAttribute('data-category-original');

      if (!matchesPerformanceCategoryName(originalName)) {
        return;
      }

      const categoryId = node.getAttribute('data-category-id');

      if (categoryId) {
        ids.add(String(categoryId));
      }
    });

  return Array.from(ids);
};

const hasExplicitConsentValue = (consents, categoryId) => {
  if (!categoryId || !consents || typeof consents !== 'object') {
    return false;
  }

  const keysToCheck = [String(categoryId), Number(categoryId)];

  for (let index = 0; index < keysToCheck.length; index += 1) {
    const key = keysToCheck[index];

    if (!Object.prototype.hasOwnProperty.call(consents, key)) {
      continue;
    }

    const value = consents[key];

    if (value === true || value === 1 || value === '1' || value === 'true') {
      return true;
    }
  }

  return false;
};

const hasExplicitDenialValue = (consents, categoryId) => {
  if (!categoryId || !consents || typeof consents !== 'object') {
    return false;
  }

  const keysToCheck = [String(categoryId), Number(categoryId)];

  for (let index = 0; index < keysToCheck.length; index += 1) {
    const key = keysToCheck[index];

    if (!Object.prototype.hasOwnProperty.call(consents, key)) {
      continue;
    }

    const value = consents[key];

    if (value === false || value === 0 || value === '0' || value === 'false') {
      return true;
    }
  }

  return false;
};

const getConsentValuesFromCookie = (parsed) => {
  if (!parsed || typeof parsed !== 'object') {
    return {};
  }

  if (parsed.consents && typeof parsed.consents === 'object') {
    return parsed.consents;
  }

  if (parsed.st && typeof parsed.st === 'object') {
    return parsed.st;
  }

  // Securiti also writes a flat cookie: {"essential":1,"performance_function":0,...,"location":"AR"}
  return parsed;
};

const getPerformanceConsentFromCookieValues = (consents) => {
  if (!consents || typeof consents !== 'object') {
    return false;
  }

  const categories = getSecuritiCategories();
  const performanceCategoryIds = getPerformanceCategoryIds();

  if (performanceCategoryIds.length > 0) {
    if (
      performanceCategoryIds.some((categoryId) =>
        hasExplicitConsentValue(consents, categoryId),
      )
    ) {
      return true;
    }

    if (
      performanceCategoryIds.some((categoryId) =>
        hasExplicitDenialValue(consents, categoryId),
      )
    ) {
      return false;
    }
  }

  const matchedConsentEntries = Object.entries(consents).filter(
    ([key, value]) => {
      if (value !== true && value !== 1 && value !== '1' && value !== 'true') {
        return false;
      }

      if (matchesPerformanceCategoryName(key)) {
        return true;
      }

      const matchedCategory = categories.find(
        (category) => String(category.id) === String(key),
      );

      return matchesPerformanceCategory(matchedCategory);
    },
  );

  return matchedConsentEntries.length > 0;
};

export const hasPerformanceFunctionalityConsent = () => {
  const rawCookie = readSecuritiConsentCookie();

  if (!rawCookie) {
    return false;
  }

  try {
    const parsed = JSON.parse(rawCookie);
    const consents = getConsentValuesFromCookie(parsed);

    return getPerformanceConsentFromCookieValues(consents);
  } catch (e) {
    return false;
  }
};

export const isSecuritiConsentReady = () => {
  if (typeof window.bannerConfigUtils?.getParams !== 'function') {
    return false;
  }

  const categories = window.bannerConfigUtils.getParams().categories;

  return Array.isArray(categories) && categories.length > 0;
};

export const isUtagLoaded = () =>
  typeof window.utag === 'object' && window.utag !== null;

export const isCrispWidgetInDom = () =>
  Boolean(document.querySelector(CRISP_WIDGET_SELECTOR));

export const hideCrispChat = () => {
  if (window.$crisp && typeof window.$crisp.push === 'function') {
    window.$crisp.push(['do', 'chat:hide']);
  }
};

// Tealium owns the Crisp tag and it only runs on `view` events. The SPA fires a
// single view on the initial page load, which normally happens on /login, where
// the tag deliberately skips Crisp. Without a second view the tag is never
// re-evaluated and the widget never appears, so ask utag for one view once the
// user is on an authenticated route with consent granted.
export const requestCrispTagFromTealium = () => {
  if (crispTagRequested) {
    return false;
  }

  if (isCrispWidgetInDom() || window.CRISP_WEBSITE_ID) {
    return false;
  }

  if (typeof window.utag?.view !== 'function') {
    return false;
  }

  crispTagRequested = true;

  try {
    window.utag.view({ ...(window.utag_data || {}) });
  } catch (e) {
    return false;
  }

  return true;
};

export const scheduleReloadAfterConsentSave = () => {
  if (consentSaveReloadScheduled) {
    return;
  }

  consentSaveReloadScheduled = true;

  const previousConsent = hasPerformanceFunctionalityConsent();
  const initialRawCookie = readRawCookie(SECURITI_CONSENT_COOKIE);
  const startedAt = Date.now();

  const checkForConsentChange = () => {
    const rawCookie = readRawCookie(SECURITI_CONSENT_COOKIE);
    const cookieChanged = rawCookie !== initialRawCookie;
    const timedOut = Date.now() - startedAt >= CONSENT_SAVE_SETTLE_TIMEOUT_MS;

    if (!cookieChanged && !timedOut) {
      window.setTimeout(checkForConsentChange, CONSENT_SAVE_SETTLE_POLL_MS);
      return;
    }

    if (hasPerformanceFunctionalityConsent() !== previousConsent) {
      window.location.reload();
      return;
    }

    // No relevant change (e.g. only other categories toggled); allow future saves.
    consentSaveReloadScheduled = false;
  };

  window.setTimeout(checkForConsentChange, CONSENT_SAVE_SETTLE_POLL_MS);
};

export const openCookiePreferencesPopup = () => {
  if (typeof window.showConsentPreferencesPopup === 'function') {
    window.showConsentPreferencesPopup();
  }
};
